'use client'

import React, { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import EditablePackageDimensionsTable from './EditablePackageDimensionsTable'
import ArmrestsConditionsTable from './ArmrestsConditionsTable'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { InfoCircledIcon, UpdateIcon } from '@radix-ui/react-icons'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { gql } from '@apollo/client'
import { GET_SOFA_FORM_DETAILS } from '../productQueries'
import { Icons } from '@/components/shared/icons'
import { useConfirmation } from '@/context/ConfirmationContext'
import { SofaFormTypeGridSelect } from '@/components/shared/inputComponents/SofaFormTypeGridSelect'
import CompositeMembersEditor from './CompositeMembersEditor'
import GeneratedBlueprintPanel from './GeneratedBlueprintPanel'
import SofaDimensionsBlueprintField from './SofaDimensionsBlueprintField'
import {
  getShapeChangeableProperties,
  type ChangeableProperties,
} from '@/configurator/utils/shapeConfigUtils'
import {
  dimensionFieldsConfig,
  DimensionFieldItem,
  isOnBlueprintKey,
  ExtensionType,
  BackrestType,
} from './dimensionFields'
import { toFormComposition } from './compositionUtils'

const UPDATE_SOFA_FORM = gql`
  mutation updateExistingSofaShape($id: Float!, $sofa_shape: SofaShapeInput!) {
    updateExistingSofaShape(id: $id, sofa_shape: $sofa_shape) {
      id
    }
  }
`

const DELETE_SOFA_SHAPE = gql`
  mutation DeleteSofaShape($id: Float!) {
    deleteSofaShape(id: $id) {
      id
    }
  }
`

const GET_SOFA_SHAPE_USAGE = gql`
  query SofaShapeUsageCount($id: Float!) {
    sofaShapeUsageCount(id: $id)
  }
`

const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  type: z.string().min(1, 'Type is required'),
  code: z.string().optional().nullable(),
  left_and_right: z.boolean(),
  duplicated: z.boolean().default(false),
  unique_marker: z.string().default(''),
  metadata: z.any().nullable().default(null),
  enabled: z.boolean().default(false),
  dimensions: z
    .object({
      height: z.number().min(0).max(1000).nullable(),
      width: z.number().min(0).max(1000).nullable(),
      length: z.number().min(0).max(1000).nullable(),
      seat_height: z.number().min(0).max(1000).nullable(),
      seat_depth: z.number().min(0).max(1000).nullable(),
      seat_width: z.number().min(0).max(1000).nullable(),
      armrest_width: z.number().min(0).max(1000).nullable(),
      backrest_width: z.number().min(0).max(1000).nullable(),
      corner_radius: z.number().min(0).max(1000).nullable(),
      number_of_big_pillows: z.number().min(0).max(100).nullable(),
      number_of_small_pillows: z.number().min(0).max(100).nullable(),
      spread_of_small_pillows: z.number().min(0).max(1000).nullable(),
      spread_of_big_pillows: z.number().min(0).max(1000).nullable(),
      size_of_big_pillow: z.number().min(0).max(1000).nullable(),
      size_of_pillow: z.number().min(0).max(1000).nullable(),
      angle: z.number().min(0).max(360).nullable(),
      extendable_part_length: z.number().min(0).max(1000).nullable(),
      mattress_width: z.number().min(0).max(1000).nullable(),
      mattress_length: z.number().min(0).max(1000).nullable(),
      corner_part_length: z.number().min(0).max(1000).nullable(),
      seat_sections: z.number().min(0).max(100).nullable(),
      backrest_sections: z.number().min(0).max(100).nullable(),
      extension_type: z.nativeEnum(ExtensionType),
      backrest_type: z.nativeEnum(BackrestType),
      covered_side: z.boolean().nullable(),
      composition: z
        .array(
          z
            .object({
              shape: z.string().min(1),
              width: z.number().min(0).max(1000).nullable(),
              height: z.number().min(0).max(1000).nullable(),
            })
            .passthrough()
        )
        .optional()
        .nullable(),
    })
    .default({
      height: null,
      width: null,
      length: null,
      seat_height: null,
      seat_depth: null,
      seat_width: null,
      armrest_width: null,
      backrest_width: null,
      corner_radius: null,
      number_of_big_pillows: null,
      number_of_small_pillows: null,
      spread_of_small_pillows: null,
      spread_of_big_pillows: null,
      size_of_big_pillow: null,
      size_of_pillow: null,
      angle: null,
      extendable_part_length: null,
      mattress_width: null,
      mattress_length: null,
      corner_part_length: null,
      seat_sections: null,
      backrest_sections: null,
      extension_type: ExtensionType.STANDART,
      backrest_type: BackrestType.STANDART,
      covered_side: null,
    }),
  package_dimensions: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
        volume: z.number(),
      })
    )
    .default([]),
})

interface SofaFormEditFormProps {
  data: {
    id: number
    name: string
    type: string
    code: string
    left_and_right: boolean
    enabled: boolean
    dimensions?: {
      height?: number
      width?: number
      length?: number
      seat_height?: number
      seat_depth?: number
      seat_width?: number
      armrest_width?: number
      backrest_width?: number
      corner_radius?: number
      number_of_big_pillows?: number
      number_of_small_pillows?: number
      spread_of_small_pillows?: number
      spread_of_big_pillows?: number
      size_of_big_pillow?: number
      size_of_pillow?: number
      angle?: number
      extendable_part_length?: number
      mattress_width?: number
      mattress_length?: number
      corner_part_length?: number
      seat_sections?: number
      backrest_sections?: number
      extension_type?: ExtensionType
      backrest_type?: BackrestType
      covered_side?: boolean
      composition?: unknown
    }
    package_dimensions?: Array<{
      id: number
      name: string
      volume: number
    }>
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

const handleMetadataUpdate = (
  metadata: any,
  enabled: boolean,
  serverEnabled: boolean
) => {
  if (enabled !== serverEnabled) {
    return {
      ...metadata,
      manualValues: {
        ...(metadata?.manualValues || {}),
        enabled: enabled,
      },
    }
  }
  return metadata
}

export default function SofaFormEditForm({
  data,
  onDirtyChange,
  onRequestClose,
}: SofaFormEditFormProps) {
  const {
    data: { findUniqueSofaForm: sofaData } = {},
    loading: queryLoading,
    error: queryError,
  } = useQuery(GET_SOFA_FORM_DETAILS, {
    variables: { id: data.id },
    fetchPolicy: 'network-only',
  })

  console.log('data', data)
  console.log('sofaData', sofaData)

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      name: data.name,
      type: data.type,
      code: data.code,
      left_and_right: data.left_and_right,
      enabled: data.enabled,
      duplicated: false,
      unique_marker: '',
      metadata: null,
      dimensions: {
        height: data.dimensions?.height ?? null,
        width: data.dimensions?.width ?? null,
        length: data.dimensions?.length ?? null,
        seat_height: data.dimensions?.seat_height ?? null,
        seat_depth: data.dimensions?.seat_depth ?? null,
        seat_width: data.dimensions?.seat_width ?? null,
        armrest_width: data.dimensions?.armrest_width ?? null,
        backrest_width: data.dimensions?.backrest_width ?? null,
        corner_radius: data.dimensions?.corner_radius ?? null,
        number_of_big_pillows: data.dimensions?.number_of_big_pillows ?? null,
        number_of_small_pillows:
          data.dimensions?.number_of_small_pillows ?? null,
        spread_of_small_pillows:
          data.dimensions?.spread_of_small_pillows ?? null,
        spread_of_big_pillows: data.dimensions?.spread_of_big_pillows ?? null,
        size_of_big_pillow: data.dimensions?.size_of_big_pillow ?? null,
        size_of_pillow: data.dimensions?.size_of_pillow ?? null,
        angle: data.dimensions?.angle ?? null,
        extendable_part_length: data.dimensions?.extendable_part_length ?? null,
        mattress_width: data.dimensions?.mattress_width ?? null,
        mattress_length: data.dimensions?.mattress_length ?? null,
        corner_part_length: data.dimensions?.corner_part_length ?? null,
        seat_sections: data.dimensions?.seat_sections ?? null,
        backrest_sections: data.dimensions?.backrest_sections ?? null,
        extension_type:
          data.dimensions?.extension_type ?? ExtensionType.STANDART,
        backrest_type: data.dimensions?.backrest_type ?? BackrestType.STANDART,
        covered_side: data.dimensions?.covered_side ?? null,
        composition: toFormComposition(data.dimensions?.composition),
      },
      package_dimensions: data.package_dimensions ?? [],
    },
  })

  const [hasManualEnabledValue, setHasManualEnabledValue] = useState(false)
  const [changeableProperties, setChangeableProperties] =
    useState<ChangeableProperties>({
      width: true,
      height: true,
      length: true,
      seat_height: true,
      seat_depth: true,
      seat_width: true,
      corner_radius: true,
      armrest_width: true,
      backrest_width: true,
      number_of_big_pillows: true,
      number_of_small_pillows: true,
      spread_of_small_pillows: true,
      spread_of_big_pillows: true,
      size_of_big_pillow: true,
      size_of_pillow: true,
      extension_type: true,
      backrest_type: true,
      angle: true,
      covered_side: true,
    })
  // Type the current changeableProperties belong to; the blueprint hides its
  // anchors until this matches the selected type (avoids an armrest input
  // flashing on armless shapes while the all-true initial state is live).
  const [propertiesType, setPropertiesType] = useState<string | null>(null)

  const [updateSofaForm, { loading: isSaving }] = useMutation(
    UPDATE_SOFA_FORM,
    {
      onError: e => {
        toast.error('Failed to update sofa form')
      },
      onCompleted: () => {
        toast.success('Sofa form updated successfully')
        // Reset form to mark it as pristine after successful submission
        form.reset(form.getValues())
      },
      refetchQueries: ['GET_PRODUCT_DETAILS'],
    }
  )

  const { showConfirmation } = useConfirmation()

  const [fetchUsageCount] = useLazyQuery(GET_SOFA_SHAPE_USAGE, {
    fetchPolicy: 'network-only',
  })

  const [deleteSofaShape, { loading: isDeleting }] = useMutation(
    DELETE_SOFA_SHAPE,
    {
      refetchQueries: ['GetSofaForms', 'GET_PRODUCT_DETAILS'],
    }
  )

  const handleDelete = async () => {
    // Warn-with-count (2026-07-29 decision): deleting an in-use module silently
    // removes it from historical carts/offers, so the dialog says how many.
    let usageCount = 0
    try {
      const result = await fetchUsageCount({ variables: { id: data.id } })
      usageCount = result.data?.sofaShapeUsageCount ?? 0
    } catch {
      // fall back to the plain copy if the count can't be fetched
    }

    showConfirmation({
      onConfirm: async () => {
        try {
          await deleteSofaShape({ variables: { id: data.id } })
          toast.success('Sofa module deleted successfully')
          onDirtyChange?.(false)
          onRequestClose?.()
        } catch (error) {
          toast.error('Failed to delete sofa module')
        }
      },
      description: usageCount
        ? `This module is used in ${usageCount} cart(s)/offer(s). Deleting it will permanently remove it from those records. This action cannot be undone.`
        : 'Are you sure you want to delete this module? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    })
  }

  const handleReset = () => {
    const currentMetadata = form.getValues('metadata') || {}
    if (currentMetadata.manualValues) {
      const { enabled, ...restManualValues } = currentMetadata.manualValues
      const newMetadata = {
        ...currentMetadata,
        manualValues: restManualValues,
      }
      form.setValue('metadata', newMetadata, { shouldDirty: true })
      form.setValue('enabled', sofaData?.enabled || false, {
        shouldDirty: true,
      })
    }
  }

  // Update metadata when enabled changes and differs from server value
  useEffect(() => {
    if (!sofaData) return
    const currentEnabled = form.watch('enabled')
    const serverEnabled = sofaData?.enabled || false

    if (currentEnabled !== serverEnabled) {
      const currentMetadata = form.getValues('metadata') || {}
      const newMetadata = handleMetadataUpdate(
        currentMetadata,
        currentEnabled,
        serverEnabled
      )
      form.setValue('metadata', newMetadata, { shouldDirty: true })
    }
  }, [form.watch('enabled'), sofaData?.enabled])

  // Check if metadata has manualValues.enabled
  useEffect(() => {
    const currentMetadata = form.getValues('metadata')
    if (!currentMetadata) {
      setHasManualEnabledValue(false)
      return
    }
    setHasManualEnabledValue(
      currentMetadata?.manualValues?.enabled !== undefined
    )
  }, [form.watch('metadata')])

  // Load changeable properties when form type changes
  useEffect(() => {
    const loadChangeableProperties = async () => {
      const currentType = form.watch('type')
      if (currentType) {
        try {
          const properties = await getShapeChangeableProperties(currentType)
          if (form.getValues('type') !== currentType) return
          setChangeableProperties(properties)
          setPropertiesType(currentType)
        } catch (error) {
          console.error('Error loading changeable properties:', error)
          // Keep default properties on error
        }
      }
    }

    loadChangeableProperties()
  }, [form.watch('type')])

  function onSubmit(values: z.infer<typeof formSchema>) {
    const metadata = handleMetadataUpdate(
      values.metadata,
      values.enabled,
      sofaData?.enabled
    )
    console.log('values', values)

    // Helper function to remove __typename from objects
    const removeTypename = (obj: any): any => {
      if (obj === null || obj === undefined) return obj
      if (Array.isArray(obj)) {
        return obj.map(removeTypename)
      }
      if (typeof obj === 'object') {
        const cleaned = { ...obj }
        delete cleaned.__typename
        // Recursively clean nested objects
        Object.keys(cleaned).forEach(key => {
          cleaned[key] = removeTypename(cleaned[key])
        })
        return cleaned
      }
      return obj
    }

    // Preserve original dimensions and only override the modified ones
    const originalDimensions = removeTypename(sofaData?.dimensions || {})
    const updatedDimensions = {
      ...originalDimensions,
      ...values.dimensions,
      // Composition is owned by form state now; always persist a real array
      // (or null), never the legacy JSON-string variant, and never null
      // member footprints.
      composition:
        values.dimensions.composition?.map(entry => ({
          ...entry,
          width: entry.width ?? 0,
          height: entry.height ?? 0,
        })) ??
        originalDimensions.composition ??
        null,
    }

    // Process package dimensions to remove temporary IDs for new dimensions
    const processedPackageDimensions = values.package_dimensions.map(
      dimension => {
        // If the ID is a timestamp (temporary ID), remove it for new dimensions
        const isTemporaryId = dimension.id > 1000000000000 // Timestamp-based IDs are much larger
        if (isTemporaryId) {
          const { id, ...dimensionWithoutId } = dimension
          return dimensionWithoutId
        }
        return dimension
      }
    )

    // Clean package_dimensions to remove __typename
    const cleanedPackageDimensions = removeTypename(processedPackageDimensions)

    updateSofaForm({
      variables: {
        id: data.id,
        sofa_shape: {
          id: data.id,
          name: values.name,
          type: values.type as any, // SofaFormType enum
          code: values.code,
          left_and_right: values.left_and_right,
          duplicated: values.duplicated,
          unique_marker: values.unique_marker,
          metadata,
          enabled: values.enabled,
          dimensions: updatedDimensions,
          package_dimensions: cleanedPackageDimensions,
          fabric_category_prices: [], // Optional field
        },
      },
    })
  }

  useEffect(() => {
    console.log('sofaData', sofaData)
    if (sofaData) {
      console.log('sofaData', sofaData)
      // Preserve current manual metadata values when resetting
      const currentMetadata = form.getValues('metadata')
      const preservedMetadata = currentMetadata?.manualValues
        ? {
            ...sofaData.metadata,
            manualValues: currentMetadata.manualValues,
          }
        : sofaData.metadata

      // Ensure the new enum fields have default values when resetting
      const resetData = {
        ...sofaData,
        metadata: preservedMetadata,
        dimensions: {
          ...sofaData.dimensions,
          extension_type:
            sofaData.dimensions?.extension_type ?? ExtensionType.STANDART,
          backrest_type:
            sofaData.dimensions?.backrest_type ?? BackrestType.STANDART,
          composition: toFormComposition(sofaData.dimensions?.composition),
        },
      }
      form.reset(resetData)
    }
  }, [sofaData, form.reset])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <SheetTitle>Edit Sofa Form</SheetTitle>
        <SheetDescription>Id: {data.id}</SheetDescription>
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Enabled"
                control={form.control}
                name="enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-baseline gap-2 py-1.5">
                    <FormControl>
                      <Switch
                        variant="green"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    {hasManualEnabledValue && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="cursor-pointer flex items-center translate-y-[1px]">
                            <InfoCircledIcon className="h-4 w-4 text-blue-500" />
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="flex flex-col gap-2 p-3">
                          <p>
                            This value is not synced with the integration server
                          </p>
                          <Button
                            variant="link"
                            size="sm"
                            className="text-red-500"
                            onClick={e => {
                              e.preventDefault()
                              handleReset()
                            }}
                          >
                            Reset
                          </Button>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <InputFieldContainer
                label="Type"
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <SofaFormTypeGridSelect field={field} />
                    <FormMessage />
                  </FormItem>
                )}
              />
              {form.watch('type') === 'COMPOSITE' && (
                <InputFieldContainer
                  label="Modules"
                  control={form.control}
                  name="dimensions.composition"
                  render={() => (
                    <FormItem className="col-span-3">
                      <CompositeMembersEditor />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <InputFieldContainer
                label="2D Blueprint"
                control={form.control}
                name="dimensions"
                render={() => (
                  <FormItem className="col-span-3">
                    <SofaDimensionsBlueprintField
                      id={data.id}
                      changeableProperties={changeableProperties}
                      propertiesType={propertiesType}
                    />
                  </FormItem>
                )}
              />

              <Separator />
              <div className=" grid grid-cols-2 gap-4">
                {dimensionFieldsConfig.map(({ key, label, measurement }) => {
                  if (!changeableProperties[key]) return null
                  if (isOnBlueprintKey(key, form.watch('type'))) return null
                  return (
                    <InputFieldContainer
                      key={key}
                      label={label}
                      variant="two-column"
                      control={form.control}
                      name={`dimensions.${key}` as any}
                      render={({ field }) => (
                        <DimensionFieldItem
                          fieldKey={key}
                          measurement={measurement}
                          field={field}
                        />
                      )}
                    />
                  )
                })}
              </div>
              <Separator />
              <InputFieldContainer
                label="Name"
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter form name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Code"
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input placeholder="Enter form code" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Packages"
                control={form.control}
                name="package_dimensions"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <EditablePackageDimensionsTable
                      dimensions={field.value || []}
                      onChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch('metadata')?.armrestsConditions && (
                <InputFieldContainer
                  label="Armrest Conditions"
                  control={form.control}
                  name="metadata.armrestsConditions"
                  render={() => (
                    <FormItem className="col-span-3">
                      <ArmrestsConditionsTable
                        conditions={
                          form.watch('metadata')?.armrestsConditions || []
                        }
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <InputFieldContainer
                label="Metadata"
                control={form.control}
                name="metadata"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Enter metadata as JSON"
                        className="font-mono min-h-[200px] md:text-xs"
                        {...field}
                        value={
                          field.value
                            ? JSON.stringify(field.value, null, 2)
                            : ''
                        }
                        onChange={e => {
                          try {
                            const value = e.target.value
                              ? JSON.parse(e.target.value)
                              : null
                            field.onChange(value)
                          } catch (error) {
                            field.onChange(e.target.value)
                          }
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-col gap-2">
                {queryError && (
                  <div className="text-red-500 text-sm">
                    Error loading sofa details: {queryError.message}
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="shrink-0"
                  >
                    <Icons.trash className="h-4 w-4 text-red-500" />
                  </Button>
                  <Button
                    type="submit"
                    disabled={queryLoading || isSaving}
                    className="flex-1"
                  >
                    {isSaving && (
                      <UpdateIcon className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>

              <Separator />
              <InputFieldContainer
                label="Generated blueprint"
                control={form.control}
                name="dimensions"
                render={() => (
                  <FormItem className="col-span-3">
                    <GeneratedBlueprintPanel
                      sofaFormId={data.id}
                      blueprint={sofaData?.blueprint}
                      loading={queryLoading}
                    />
                  </FormItem>
                )}
              />

            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
